import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSetAtom } from "jotai";
import Queries from "../../params/queries";
import Mutations from "../../params/mutations";
import type {
  AccountRow,
  ApiResult,
  ChatMessage,
  ConfirmPopupRequest,
  Conversation,
  FetchApi,
  FetchMultipart,
  OutboundRow,
} from "../../params/types";
import { activePopupState } from "../../state/components/popup";
import {
  conversationPeerKey,
  dedupeConversations,
  resolveThreadIdForPeer,
} from "./helpers";
import { ThreadList } from "./thread-list";
import { ThreadView } from "./thread-view";
import { Compose } from "./compose";

const CONVO_PAGE = 80;

export interface InboxProps {
  accounts: AccountRow[];
  initialAccount?: string | null;
  fetchApi: FetchApi;
  fetchMultipart?: FetchMultipart | null;
  busy?: boolean;
  setBusy: (busy: boolean) => void;
  setError: (message: string) => void;
  flashSuccess: (message: string) => void;
  liveRevision?: number;
  processRunning?: boolean;
  setConfirmPopup?: ((info: ConfirmPopupRequest) => void) | null;
  onOpenAccountSettings?: ((account: AccountRow) => void) | null;
}

/**
 * Inbox UI: pick account → conversations → messages + compose.
 */
export default function Inbox({
  accounts,
  initialAccount,
  fetchApi,
  fetchMultipart,
  busy,
  setBusy,
  setError,
  flashSuccess,
  liveRevision = 0,
  processRunning: _processRunning = false,
  setConfirmPopup = null,
  onOpenAccountSettings = null,
}: InboxProps) {
  const setScheduledPopup = useSetAtom(activePopupState("scheduled_outbound"));
  const [selectedAccount, setSelectedAccount] = useState(
    () => initialAccount || accounts[0]?.username || "",
  );
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [threadDetail, setThreadDetail] = useState<Conversation | null>(null);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [loadingConvos, setLoadingConvos] = useState(
    () => Boolean(initialAccount || accounts[0]?.username),
  );
  const [threadLoadedId, setThreadLoadedId] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreConvos, setHasMoreConvos] = useState(false);
  const [draft, setDraft] = useState("");
  const [newPeer, setNewPeer] = useState("");
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [failedOutbound, setFailedOutbound] = useState<OutboundRow[]>([]);
  const [threadPrompt, setThreadPrompt] = useState("");
  const [promptDirty, setPromptDirty] = useState(false);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [attachFile, setAttachFile] = useState<File | null>(null);
  const [railExpanded, setRailExpanded] = useState(false);
  const [contactsRescanning, setContactsRescanning] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const chatMessagesRef = useRef<HTMLDivElement | null>(null);
  const chatContentRef = useRef<HTMLDivElement | null>(null);
  const chatPinnedToBottomRef = useRef(true);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedThreadIdRef = useRef<string | null>(null);
  const [seenInitialAccount, setSeenInitialAccount] = useState(initialAccount);
  const [accountForThreadUi, setAccountForThreadUi] = useState(selectedAccount);

  // Parent jumped to Inbox with a specific account — adjust during render.
  if (initialAccount && initialAccount !== seenInitialAccount) {
    setSeenInitialAccount(initialAccount);
    setSelectedAccount(initialAccount);
  }

  // Keep selection valid against the live accounts list.
  const fallbackAccount = accounts[0]?.username || "";
  const accountStillValid =
    !selectedAccount ||
    !accounts.length ||
    accounts.some((a) => a.username === selectedAccount);
  if (!accountStillValid) {
    setSelectedAccount(fallbackAccount);
  } else if (!selectedAccount && fallbackAccount) {
    setSelectedAccount(fallbackAccount);
  }

  // Reset thread UI when the selected account changes (render-time adjust).
  if (selectedAccount !== accountForThreadUi) {
    setAccountForThreadUi(selectedAccount);
    setSelectedThreadId(null);
    setThreadDetail(null);
    setDraft("");
    setThreadPrompt("");
    setPromptDirty(false);
    setSearchInput("");
    setSearch("");
    setFilter("all");
  }

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(searchInput.trim());
    }, 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchInput]);

  useEffect(() => {
    selectedThreadIdRef.current = selectedThreadId;
  }, [selectedThreadId]);

  useEffect(() => {
    if (!selectedAccount || !selectedThreadId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset thread detail when nothing is selected
      setThreadDetail(null);
      setThreadLoadedId(null);
      return undefined;
    }
    let cancelled = false;
    const threadId = selectedThreadId;
    fetchApi(Queries.Conversation, {
      account: selectedAccount,
      thread_id: threadId,
    }).then((res) => {
      if (cancelled) return;
      if (!res?.errors && res?.conversation) {
        setThreadDetail(res.conversation);
      }
      setThreadLoadedId(threadId);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedAccount, selectedThreadId, liveRevision, fetchApi]);

  // Viewing a conversation marks it read (dot + account badge).
  const markThreadRead = useCallback(
    (account?: string | null, threadId?: string | null) => {
      if (!account || !threadId) return;
      if (String(threadId).startsWith("peer:")) return;
      setConversations((prev) =>
        prev.map((row) =>
          row.thread_id === threadId ? { ...row, unread: false } : row,
        ),
      );
      fetchApi(Mutations.MarkConversationRead, {
        account,
        thread_id: threadId,
      }).catch(() => {});
    },
    [fetchApi],
  );

  const loadConversations = useCallback(
    (account: string, { soft = false }: { soft?: boolean } = {}) => {
      if (!account) {
        setConversations([]);
        setHasMoreConvos(false);
        setLoadingConvos(false);
        return Promise.resolve();
      }
      if (!soft) setLoadingConvos(true);
      const vars: Record<string, unknown> = {
        account,
        limit: CONVO_PAGE,
        offset: 0,
      };
      if (filter && filter !== "all") vars.filter = filter;
      if (search) vars.search = search;
      return fetchApi(Queries.Conversations, vars).then((res) => {
        if (res?.errors) {
          setError(res.errors[0]?.message || "Failed to load conversations");
        } else {
          const next = dedupeConversations(res?.conversations || []);
          setConversations(next);
          setHasMoreConvos(next.length >= CONVO_PAGE);
          // If we were on a synthetic peer: row, snap selection to the real thread.
          let resolved = selectedThreadIdRef.current;
          if (resolved && String(resolved).startsWith("peer:")) {
            const peer = String(resolved).slice(5).toLowerCase();
            const real = next.find(
              (c) =>
                !String(c.thread_id).startsWith("peer:") &&
                conversationPeerKey(c) === peer,
            );
            if (real?.thread_id) {
              resolved = real.thread_id;
              selectedThreadIdRef.current = resolved;
              setSelectedThreadId(resolved);
            }
          }
          // A message that arrived in the open thread is already being read.
          if (next.find((c) => c.thread_id === resolved)?.unread) {
            markThreadRead(account, resolved);
          }
        }
        if (!soft) setLoadingConvos(false);
      });
    },
    [fetchApi, setError, filter, search, markThreadRead],
  );

  const loadMoreConversations = useCallback(() => {
    if (!selectedAccount || loadingMore || loadingConvos || !hasMoreConvos) {
      return;
    }
    setLoadingMore(true);
    const vars: Record<string, unknown> = {
      account: selectedAccount,
      limit: CONVO_PAGE,
      offset: conversations.length,
    };
    if (filter && filter !== "all") vars.filter = filter;
    if (search) vars.search = search;
    fetchApi(Queries.Conversations, vars)
      .then((res) => {
        if (res?.errors) return;
        const extra = res?.conversations || [];
        setHasMoreConvos(extra.length >= CONVO_PAGE);
        setConversations((prev) =>
          dedupeConversations([...prev, ...extra]),
        );
      })
      .finally(() => setLoadingMore(false));
  }, [
    selectedAccount,
    loadingMore,
    loadingConvos,
    hasMoreConvos,
    conversations.length,
    filter,
    search,
    fetchApi,
  ]);

  const loadFailedOutbound = useCallback(
    (account: string) => {
      if (!account) {
        setFailedOutbound([]);
        return Promise.resolve();
      }
      return fetchApi(Queries.Outbound, {
        account,
        status: "failed",
        limit: 50,
      }).then((res) => {
        if (res?.errors) return;
        setFailedOutbound(res?.outbound || []);
      });
    },
    [fetchApi],
  );

  // Initial / account-change fetch — setState for loading flags is intentional.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- data-fetching effect
    loadConversations(selectedAccount);
    loadFailedOutbound(selectedAccount);
  }, [selectedAccount, loadConversations, loadFailedOutbound]);

  // Soft-refresh inbox when the live dashboard WS publishes (no interval poll).
  useEffect(() => {
    if (!selectedAccount || !liveRevision) return;
    // Soft inbox refresh driven by dashboard WS — loading flags are intentional.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- live WS refresh
    loadConversations(selectedAccount, { soft: true });
    loadFailedOutbound(selectedAccount);
  }, [liveRevision, selectedAccount, loadConversations, loadFailedOutbound]);

  useEffect(() => {
    const acc = accounts.find((a) => a.username === selectedAccount);
    if (!acc) return;
    // progress_detail is overwritten within seconds by the inbox/followers
    // steps, so the rescan flag has to come from the queued job itself.
    const rescanning =
      String(acc.contacts_rescan_status || "").toLowerCase() === "rescanning";
    // eslint-disable-next-line react-hooks/set-state-in-effect -- live WS refresh
    setContactsRescanning(rescanning);
  }, [accounts, selectedAccount, liveRevision]);

  // While any bubble is still sending, soft-poll so the UI flips to delivered live.
  const hasSendingBubble = (threadDetail?.messages || []).some(
    (m) =>
      m.source === "sending" ||
      m.source === "queued" ||
      m.source === "scheduled" ||
      m.source === "paused" ||
      String(m.id || "").startsWith("local:"),
  );
  useEffect(() => {
    if (!selectedAccount || !hasSendingBubble) return undefined;
    const id = setInterval(() => {
      loadConversations(selectedAccount, { soft: true });
      loadFailedOutbound(selectedAccount);
      const tid = selectedThreadIdRef.current;
      if (tid) {
        fetchApi(Queries.Conversation, {
          account: selectedAccount,
          thread_id: tid,
        }).then((res) => {
          if (res?.conversation) setThreadDetail(res.conversation);
        });
      }
    }, 2500);
    return () => clearInterval(id);
  }, [
    selectedAccount,
    hasSendingBubble,
    loadConversations,
    loadFailedOutbound,
    fetchApi,
  ]);

  const selected =
    (threadDetail?.thread_id === selectedThreadId ? threadDetail : null) ||
    conversations.find((c) => c.thread_id === selectedThreadId) ||
    null;
  const threadFailed = failedOutbound.filter((o) => {
    if (!selected) return false;
    if (o.thread_id && selected.thread_id && o.thread_id === selected.thread_id)
      return true;
    const peer = (selected.peer_username || "").toLowerCase();
    return peer && o.peer_username && o.peer_username.toLowerCase() === peer;
  });
  // Keep draft prompt in sync with selection without an effect (avoids set-state-in-effect).
  const displayedThreadPrompt = promptDirty
    ? threadPrompt
    : selected?.ai_prompt || "";

  const jumpChatToLatest = () => {
    const el = chatMessagesRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    chatPinnedToBottomRef.current = true;
  };

  const onChatMessagesScroll = () => {
    const el = chatMessagesRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    chatPinnedToBottomRef.current = dist < 96;
  };

  // Opening a thread: jump instantly (no smooth animation).
  useEffect(() => {
    if (!selectedThreadId) return undefined;
    chatPinnedToBottomRef.current = true;
    jumpChatToLatest();
    const raf = requestAnimationFrame(() => jumpChatToLatest());
    const t1 = setTimeout(jumpChatToLatest, 50);
    const t2 = setTimeout(jumpChatToLatest, 250);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [selectedThreadId]);

  // New bubbles / failed outbound: stay at latest while pinned.
  useEffect(() => {
    if (!chatPinnedToBottomRef.current) return;
    jumpChatToLatest();
  }, [selected?.messages?.length, threadFailed.length]);

  // Media / layout growth — keep true end while pinned (media often expands after first paint).
  useEffect(() => {
    const scroller = chatMessagesRef.current;
    const content = chatContentRef.current;
    if (!scroller || !content || !selectedThreadId) return undefined;
    const ro = new ResizeObserver(() => {
      if (!chatPinnedToBottomRef.current) return;
      scroller.scrollTop = scroller.scrollHeight;
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, [selectedThreadId]);

  const onSend = async (
    e: React.SyntheticEvent,
    { asVoice = false }: { asVoice?: boolean } = {},
  ) => {
    e.preventDefault();
    const text = draft.trim();
    if ((!text && !attachFile) || !selectedAccount) return;
    if (asVoice && attachFile) return;

    const peer = selected?.peer_username || newPeer.trim().replace(/^@/, "");
    const threadId = selected?.thread_id || null;
    if (!threadId && !peer) {
      setError("Select a conversation or enter a username to message");
      return;
    }

    const targetThreadId = resolveThreadIdForPeer(
      conversations,
      peer,
      threadId,
    );
    const optimisticId = `local:${crypto.randomUUID()}`;
    const localPreview = attachFile ? URL.createObjectURL(attachFile) : null;
    const mediaKind = attachFile?.type?.startsWith("video/")
      ? "video"
      : attachFile
        ? "image"
        : null;
    const optimisticMsg: ChatMessage = {
      id: optimisticId,
      from: "me",
      text: asVoice
        ? text || "[voice]"
        : text || (mediaKind === "video" ? "[video]" : "[photo]"),
      at: new Date().toISOString(),
      source: "sending",
      media_name: attachFile?.name || null,
      media_url: localPreview,
      media_kind: asVoice ? "voice" : mediaKind,
    };

    // Live sending bubble immediately (before the network round-trip).
    chatPinnedToBottomRef.current = true;
    setConversations((prev) => {
      const peerKey = String(peer || "")
        .replace(/^@/, "")
        .trim()
        .toLowerCase();
      const idx = prev.findIndex((c) => {
        if (c.thread_id === targetThreadId) return true;
        if (peerKey && conversationPeerKey(c) === peerKey) return true;
        return false;
      });
      if (idx < 0) {
        if (!peer) return prev;
        return dedupeConversations([
          {
            account: selectedAccount,
            thread_id: targetThreadId as string,
            title: peer,
            peer_username: peer,
            href: null,
            unread: false,
            manual_mode: Boolean(selected?.manual_mode),
            ai_prompt: null,
            messages: [optimisticMsg],
            last_preview: optimisticMsg.text,
            last_from: "me",
            last_message_at: optimisticMsg.at,
            has_failed_outbound: false,
            is_follower_only: Boolean(
              String(targetThreadId).startsWith("peer:"),
            ),
          },
          ...prev,
        ]);
      }
      const next = [...prev];
      const row = next[idx];
      next[idx] = {
        ...row,
        thread_id: String(row.thread_id).startsWith("peer:")
          ? (targetThreadId as string)
          : row.thread_id,
        messages: [...(row.messages || []), optimisticMsg],
        last_preview: optimisticMsg.text,
        last_from: "me",
        last_message_at: optimisticMsg.at,
        manual_mode: Boolean(row.manual_mode),
        is_follower_only: false,
      };
      return dedupeConversations(next);
    });
    setThreadDetail((prev) => {
      if (prev && prev.thread_id === targetThreadId) {
        return {
          ...prev,
          messages: [...(prev.messages || []), optimisticMsg],
          last_preview: optimisticMsg.text,
          last_from: "me",
          last_message_at: optimisticMsg.at,
        };
      }
      if (selected && selected.thread_id === targetThreadId) {
        return {
          ...selected,
          messages: [...(selected.messages || []), optimisticMsg],
          last_preview: optimisticMsg.text,
          last_from: "me",
          last_message_at: optimisticMsg.at,
        };
      }
      return prev;
    });
    if (targetThreadId) setSelectedThreadId(targetThreadId);
    if (!selected && peer) {
      setNewPeer("");
    }
    setDraft("");
    const fileToSend = attachFile;
    setAttachFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";

    setSending(true);
    setBusy(true);
    setError("");
    try {
      const input = {
        account: selectedAccount,
        text: text || null,
        media: null,
        thread_id:
          targetThreadId && !String(targetThreadId).startsWith("peer:")
            ? targetThreadId
            : null,
        peer_username: peer || null,
        run_if_stopped: true,
        send_as_voice: asVoice && Boolean(text),
      };

      let res: ApiResult | undefined;
      if (fileToSend) {
        if (!fetchMultipart) {
          setError("Media upload is not available");
          setConversations((prev) =>
            prev.map((c) =>
              c.thread_id === targetThreadId
                ? {
                    ...c,
                    messages: (c.messages || []).filter(
                      (m) => m.id !== optimisticId,
                    ),
                  }
                : c,
            ),
          );
          if (localPreview) URL.revokeObjectURL(localPreview);
          return;
        }
        res = await fetchMultipart(
          Mutations.SendDm,
          { input },
          { "variables.input.media": fileToSend },
        );
      } else {
        res = await fetchApi(Mutations.SendDm, { input });
      }
      if (res?.errors) {
        setError(res.errors[0]?.message || "Send failed");
        setConversations((prev) =>
          prev.map((c) =>
            c.thread_id === targetThreadId
              ? {
                  ...c,
                  messages: (c.messages || []).filter(
                    (m) => m.id !== optimisticId,
                  ),
                }
              : c,
          ),
        );
        if (localPreview) URL.revokeObjectURL(localPreview);
        return;
      }
      const result = res?.send_dm;
      if (result && result.success === false) {
        setError(result.message || "Send failed");
        setConversations((prev) =>
          prev.map((c) =>
            c.thread_id === targetThreadId
              ? {
                  ...c,
                  messages: (c.messages || []).filter(
                    (m) => m.id !== optimisticId,
                  ),
                }
              : c,
          ),
        );
        if (localPreview) URL.revokeObjectURL(localPreview);
        return;
      }
      flashSuccess(result?.message || "Sending…");
      await loadConversations(selectedAccount, { soft: true });
      await loadFailedOutbound(selectedAccount);
      if (localPreview) URL.revokeObjectURL(localPreview);
    } finally {
      setSending(false);
      setBusy(false);
    }
  };

  const onToggleManual = async () => {
    if (!selected || !selectedAccount) return;
    if (String(selected.thread_id).startsWith("peer:")) {
      setError("Sync the thread before toggling manual mode");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const next = !selected.manual_mode;
      const res = await fetchApi(Mutations.SetThreadManual, {
        account: selectedAccount,
        thread_id: selected.thread_id,
        manual: next,
      });
      if (res?.errors) {
        setError(res.errors[0]?.message || "Failed to update thread");
        return;
      }
      flashSuccess(next ? "Manual takeover enabled" : "AI resumed");
      await loadConversations(selectedAccount, { soft: true });
    } finally {
      setBusy(false);
    }
  };

  const onSaveThreadPrompt = async () => {
    if (!selected || !selectedAccount) return;
    if (String(selected.thread_id).startsWith("peer:")) {
      setError("Sync the thread before setting an AI prompt");
      return;
    }
    setSavingPrompt(true);
    setBusy(true);
    setError("");
    const nextPrompt = displayedThreadPrompt.trim() || null;
    try {
      const res = await fetchApi(Mutations.SetThreadPrompt, {
        account: selectedAccount,
        thread_id: selected.thread_id,
        ai_prompt: nextPrompt,
      });
      if (res?.errors) {
        setError(res.errors[0]?.message || "Failed to save thread prompt");
        return;
      }
      // Optimistic update so clearing promptDirty does not flash the old value.
      setConversations((prev) =>
        prev.map((c) =>
          c.thread_id === selected.thread_id
            ? { ...c, ai_prompt: nextPrompt }
            : c,
        ),
      );
      setThreadPrompt(nextPrompt || "");
      setPromptDirty(false);
      flashSuccess(
        nextPrompt ? "Thread AI prompt saved" : "Thread AI prompt cleared",
      );
      await loadConversations(selectedAccount, { soft: true });
    } finally {
      setSavingPrompt(false);
      setBusy(false);
    }
  };

  const onClearConversationHistory = () => {
    if (!selected || !selectedAccount) return;
    if (String(selected.thread_id).startsWith("peer:")) {
      setError("Sync the thread before clearing history");
      return;
    }
    const threadId = selected.thread_id;
    const peer =
      selected.peer_username ||
      String(selected.title || "")
        .split(/[\s\n]/)[0]
        .replace(/^@/, "") ||
      "user";
    const run = async () => {
      setBusy(true);
      setError("");
      try {
        const res = await fetchApi(Mutations.RescanConversation, {
          account: selectedAccount,
          thread_id: threadId,
          mode: "full",
          clear_all: true,
        });
        if (res?.errors) {
          setError(res.errors[0]?.message || "Clear history failed");
          return;
        }
        const result = res?.rescan_conversation;
        if (result?.success === false) {
          setError(result.message || "Clear history failed");
          return;
        }
        // Optimistic empty thread so the UI matches the database immediately.
        setConversations((prev) =>
          prev.map((c) =>
            c.thread_id === threadId
              ? {
                  ...c,
                  messages: [],
                  last_preview: "",
                  last_from: null,
                  ...(result?.conversation || {}),
                }
              : c,
          ),
        );
        flashSuccess(result?.message || "History cleared");
        await loadConversations(selectedAccount, { soft: true });
      } finally {
        setBusy(false);
      }
    };
    const message =
      `Clear chat history for @${peer}? ` +
      "Stored messages are removed now; the bot will rescan this thread on the next run.";
    if (typeof setConfirmPopup === "function") {
      setConfirmPopup({
        active: true,
        message,
        onConfirm: () => {
          run();
        },
      });
      return;
    }
    if (window.confirm(message)) run();
  };

  const onRescanFullHistory = () => {
    if (!selected || !selectedAccount) return;
    if (String(selected.thread_id).startsWith("peer:")) {
      setError("Sync the thread before rescanning history");
      return;
    }
    const threadId = selected.thread_id;
    const peer =
      selected.peer_username ||
      String(selected.title || "")
        .split(/[\s\n]/)[0]
        .replace(/^@/, "") ||
      "user";
    const run = async () => {
      setBusy(true);
      setError("");
      try {
        const res = await fetchApi(Mutations.RescanConversation, {
          account: selectedAccount,
          thread_id: threadId,
          mode: "full",
          clear_all: false,
        });
        if (res?.errors) {
          setError(res.errors[0]?.message || "Full rescan failed");
          return;
        }
        const result = res?.rescan_conversation;
        if (result?.success === false) {
          setError(result.message || "Full rescan failed");
          return;
        }
        flashSuccess(result?.message || "Full history rescan queued");
        await loadConversations(selectedAccount, { soft: true });
      } finally {
        setBusy(false);
      }
    };
    const message =
      `Rescan full Instagram history for @${peer}? ` +
      "Stored messages stay; the bot will scroll the whole chat and merge anything missing.";
    if (typeof setConfirmPopup === "function") {
      setConfirmPopup({
        active: true,
        message,
        onConfirm: () => {
          run();
        },
      });
      return;
    }
    if (window.confirm(message)) run();
  };

  const onClearContacts = () => {
    if (!selectedAccount) return;
    const run = async () => {
      setBusy(true);
      setError("");
      setContactsRescanning(true);
      try {
        const res = await fetchApi(Mutations.ClearAccountContacts, {
          account: selectedAccount,
        });
        if (res?.errors) {
          setError(res.errors[0]?.message || "Clear contacts failed");
          setContactsRescanning(false);
          return;
        }
        const result = res?.clear_account_contacts;
        if (result?.success === false) {
          setError(result.message || "Clear contacts failed");
          setContactsRescanning(false);
          return;
        }
        setConversations([]);
        setSelectedThreadId(null);
        selectedThreadIdRef.current = null;
        setThreadDetail(null);
        flashSuccess(result?.message || "Contacts cleared — rescanning");
      } finally {
        setBusy(false);
      }
    };
    const message =
      "Clear all contacts for this account?\n\n" +
      "This will permanently delete stored conversations, messages, outbound queue rows, " +
      "and the known-followers list for this account, then rescrape Instagram inbox + followers.\n\n" +
      "The global blocklist (Settings) is kept. The bot will show Rescanning until done.";
    if (typeof setConfirmPopup === "function") {
      setConfirmPopup({
        active: true,
        message,
        onConfirm: () => {
          run();
        },
      });
      return;
    }
    if (window.confirm(message)) run();
  };

  const onRetryOutbound = async (id: string) => {
    setBusy(true);
    setError("");
    try {
      const res = await fetchApi(Mutations.RetryOutbound, { id });
      if (res?.errors) {
        setError(res.errors[0]?.message || "Retry failed");
        return;
      }
      flashSuccess("Retrying…");
      await loadFailedOutbound(selectedAccount);
      await loadConversations(selectedAccount, { soft: true });
    } finally {
      setBusy(false);
    }
  };

  const onDiscardOutbound = async (id: string) => {
    setBusy(true);
    setError("");
    try {
      const res = await fetchApi(Mutations.DiscardOutbound, { id });
      if (res?.errors) {
        setError(res.errors[0]?.message || "Discard failed");
        return;
      }
      flashSuccess("Outbound discarded");
      await loadFailedOutbound(selectedAccount);
      await loadConversations(selectedAccount, { soft: true });
    } finally {
      setBusy(false);
    }
  };

  const refreshAfterOutboundAction = async () => {
    await loadFailedOutbound(selectedAccount);
    await loadConversations(selectedAccount, { soft: true });
  };

  const onOpenScheduledOutbound = (message: ChatMessage) => {
    const id = String(message.outbound_id || "").trim();
    if (!id) return;
    setScheduledPopup({
      id: "scheduled_outbound",
      active: true,
      chatMessage: message,
      onSendNow: async () => {
        setBusy(true);
        try {
          const res = await fetchApi(Mutations.SendOutboundNow, { id });
          if (res?.errors) {
            setError(res.errors[0]?.message || "Send now failed");
            return;
          }
          flashSuccess("Sending now…");
          await refreshAfterOutboundAction();
        } finally {
          setBusy(false);
        }
      },
      onPause: async () => {
        setBusy(true);
        try {
          const res = await fetchApi(Mutations.PauseOutbound, { id });
          if (res?.errors) {
            setError(res.errors[0]?.message || "Pause failed");
            return;
          }
          flashSuccess("Message paused");
          await refreshAfterOutboundAction();
        } finally {
          setBusy(false);
        }
      },
      onCancel: async () => {
        setBusy(true);
        try {
          const res = await fetchApi(Mutations.CancelOutbound, { id });
          if (res?.errors) {
            setError(res.errors[0]?.message || "Cancel failed");
            return;
          }
          flashSuccess("Scheduled message canceled");
          await refreshAfterOutboundAction();
        } finally {
          setBusy(false);
        }
      },
      onEdit: async (text: string) => {
        setBusy(true);
        try {
          const res = await fetchApi(Mutations.EditOutbound, { id, text });
          if (res?.errors) {
            setError(res.errors[0]?.message || "Edit failed");
            return;
          }
          flashSuccess("Message updated");
          await refreshAfterOutboundAction();
        } finally {
          setBusy(false);
        }
      },
      onReschedule: async (send_at: string) => {
        setBusy(true);
        try {
          const res = await fetchApi(Mutations.RescheduleOutbound, {
            id,
            send_at,
          });
          if (res?.errors) {
            setError(res.errors[0]?.message || "Reschedule failed");
            return;
          }
          flashSuccess("Message rescheduled");
          await refreshAfterOutboundAction();
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const chatPeerName =
    selected?.peer_username || newPeer.trim().replace(/^@/, "") || "user";

  return (
    <div className="ib-inbox ib-msg-layout">
      <ThreadList
        accounts={accounts}
        selectedAccount={selectedAccount}
        setSelectedAccount={setSelectedAccount}
        conversations={conversations}
        selectedThreadId={selectedThreadId}
        selectedThreadIdRef={selectedThreadIdRef}
        setSelectedThreadId={setSelectedThreadId}
        setPromptDirty={setPromptDirty}
        setNewPeer={setNewPeer}
        markThreadRead={markThreadRead}
        loadingConvos={loadingConvos}
        hasMoreConvos={hasMoreConvos}
        loadingMore={loadingMore}
        onLoadMore={loadMoreConversations}
        railExpanded={railExpanded}
        setRailExpanded={setRailExpanded}
        contactsRescanning={contactsRescanning}
        onClearContacts={onClearContacts}
      />

      <ThreadView
        accounts={accounts}
        selectedAccount={selectedAccount}
        selected={selected}
        newPeer={newPeer}
        conversations={conversations}
        chatPeerName={chatPeerName}
        threadFailed={threadFailed}
        busy={busy}
        displayedThreadPrompt={displayedThreadPrompt}
        setThreadPrompt={setThreadPrompt}
        setPromptDirty={setPromptDirty}
        promptDirty={promptDirty}
        savingPrompt={savingPrompt}
        onToggleManual={onToggleManual}
        onSaveThreadPrompt={onSaveThreadPrompt}
        onClearConversationHistory={onClearConversationHistory}
        onRescanFullHistory={onRescanFullHistory}
        onOpenAccountSettings={onOpenAccountSettings}
        onRetryOutbound={onRetryOutbound}
        onDiscardOutbound={onDiscardOutbound}
        onOpenScheduledOutbound={onOpenScheduledOutbound}
        chatMessagesRef={chatMessagesRef}
        chatContentRef={chatContentRef}
        onChatMessagesScroll={onChatMessagesScroll}
        loadingConvos={loadingConvos}
        loadingMessages={
          Boolean(selectedThreadId) && threadLoadedId !== selectedThreadId
        }
      >
        <Compose
          draft={draft}
          setDraft={setDraft}
          attachFile={attachFile}
          setAttachFile={setAttachFile}
          fileInputRef={fileInputRef}
          busy={busy}
          sending={sending}
          selectedAccount={selectedAccount}
          selected={Boolean(selected)}
          onSend={onSend}
        />
      </ThreadView>
    </div>
  );
}
